import React from "react";
//#region redux
import { useDispatch } from "react-redux";
import { supportResponseSubmitted, supportTicketConfirmed, supportResolved } from "store/actions/supportActions";
//endregion
//#region types
import { StyleEnums } from "models/enums";
import { SupportIssueFooterProps } from "models/propTypes/supportIssuePropTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import { PageFooter } from "components/common";
import SupportIssueMoreActions from "./moreActions";
import useSupportMessageResolveConfirm from "./useConfirm";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const SupportIssueFooter: React.FC<SupportIssueFooterProps> = (props) => {
  const {
    supportIssue,
    reloadHandler,
    onSubmitTicket,
    disableSuccessMessages,
    feedbackWasSubmitted,
    isFormFilled,
    enableResolvedCaseConfirmation,
  } = props;
  const dispatch = useDispatch();

  const { onConfirm, onResolve, getCanClear } = useSupportMessageResolveConfirm();

  const canClear = getCanClear(
    props.supportIssue.state,
    props.supportIssue.isUnread,
    props.supportIssue.isUnreadByCurrentUser
  );

  const onlyOneMessage = supportIssue?.comments?.length === 1;

  const onSendSuccess = () => {
    dispatch(supportResponseSubmitted(props.supportIssue.id));
    props.reloadHandler();
  };

  const onResolveSuccess = () => {
    dispatch(supportResolved(props.supportIssue.id));
    props.reloadHandler();
  };

  const onConfirmSuccess = () => {
    dispatch(supportTicketConfirmed(props.supportIssue.id));
    props.reloadHandler();
  };

  const supportSendAction = {
    text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Send),
    onClick: onSubmitTicket,
    type: StyleEnums.DisplayType.Success,
    onActionSuccess: onSendSuccess,
    successMessage: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.ClearedSuccessMessage),
  };

  const supportMarkAsDoneAction = {
    text: LocalizationHelperInstance.getFormattedMessage(
      supportIssue.isReadOnly ? TranslationKeys.Common.Done : TranslationKeys.Common.MarkAsDone
    ),
    onClick: onResolve.bind(null, supportIssue.id),
    type: StyleEnums.DisplayType.Success,
    onActionSuccess: onResolveSuccess,
    successMessage: LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.SupportIssue.MarkAsResolvedSuccessMessage
    ),
  };

  const supportDoneAction = {
    text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Done),
    onClick: onConfirm.bind(null, supportIssue.id),
    type: StyleEnums.DisplayType.Success,
    onActionSuccess: onConfirmSuccess,
    successMessage: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.ClearedSuccessMessage),
  };

  const getActions = () => {
    if (isFormFilled) return [supportSendAction];

    if (supportIssue.isReadOnly) {
      return supportIssue?.state === WintSuperkollAPIModel.SupportIssueState.Closed ? [] : [supportMarkAsDoneAction];
    }

    switch (supportIssue?.state) {
      case WintSuperkollAPIModel.SupportIssueState.Closed:
        return canClear && enableResolvedCaseConfirmation ? [supportDoneAction] : [];
      case WintSuperkollAPIModel.SupportIssueState.AtSupport:
        return [supportMarkAsDoneAction];
      case WintSuperkollAPIModel.SupportIssueState.AtCustomer:
        return feedbackWasSubmitted || onlyOneMessage ? [supportMarkAsDoneAction] : [supportSendAction];
      default:
        return [];
    }
  };

  return (
    <PageFooter
      allowForReadOnlyUsers
      actions={getActions()}
      moreActions={<SupportIssueMoreActions supportIssue={props.supportIssue} reloadHandler={reloadHandler} />}
      disableSuccessMessages={disableSuccessMessages}
    />
  );
};

export default SupportIssueFooter;
